import { createMultiStyleConfigHelpers, ThemingProps } from '@chakra-ui/react'
import { textStyleKeys } from '~/themes/textStyles'
import { Size } from '~/types/ThemeTypes'
import { useCustomMultiStyleConfig } from '~/utils/chakraUtils'
import { getName } from '~/utils/objectUtils'

// import { callsComponentColorKeys } from './_colors'

// #region CallsListItemStyle
const callsListItemStyleParts = ['container', 'rowWrapper'] as const
const helpers = createMultiStyleConfigHelpers(callsListItemStyleParts)

export const CallsListItemStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'flex',
      height: '4.75rem',
      px: '1rem',
      marginTop: '-2px',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '0.625rem',
      alignSelf: 'stretch',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      border: '1px solid',
      borderColor: 'transparent',
      _hover: {
        borderColor: 'gray.400',
        bg: 'gray.300',
        '& > div': {
          borderBottomColor: 'transparent',
        },
      },
      _selected: {
        borderColor: 'gray.400',
        bg: 'gray.300',
      },
      '&[data-first]': {
        marginTop: 0,
      },
    },
    rowWrapper: {
      display: 'flex',
      alignItems: 'center',
      flex: '1 0 0',
      alignSelf: 'stretch',
      borderBottom: '1px solid',
      borderBottomColor: 'gray.400',
      _hover: {
        borderBottomColor: 'transparent',
      },
    },
  },
  sizes: {
    [Size.sm]: {
      container: {
        px: '0.75rem',
      },
    },
  },
})

const themeKey = getName({ CallsListItemStyle })
export const useCallsListItemStyles = (props?: ThemingProps) =>
  useCustomMultiStyleConfig<typeof callsListItemStyleParts>(themeKey, props)
// #endregion CallsListItemStyle

// #region DateTimeCellStyle
const dateTimeCellStyleParts = [
  'container',
  'timeWrapper',
  'timeText',
  'dateText',
] as const
const dateTimeHelpers = createMultiStyleConfigHelpers(dateTimeCellStyleParts)

export const DateTimeCellStyle = dateTimeHelpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'flex',
      pr: '1rem',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      flex: '1 0 0',
    },
    timeWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
    },
    timeText: {
      textStyle: textStyleKeys.Body1,
    },
    dateText: {
      textStyle: textStyleKeys.Body3,
      color: 'gray.800',
    },
  },
})

const dateTimeThemeKey = getName({ DateTimeCellStyle })
export const useDateTimeCellStyles = (props?: ThemingProps) =>
  useCustomMultiStyleConfig<typeof dateTimeCellStyleParts>(
    dateTimeThemeKey,
    props,
  )
// #endregion DateTimeCellStyle

// #region OutcomeCellStyle
export enum CallOutcomeCellVariant {
  InProgress = 'inProgress',
  Success = 'success',
  Failure = 'failure',
}

const outcomeCellStyleParts = [
  'container',
  'iconWrapper',
  'icon',
  'text',
] as const
const outcomeHelpers = createMultiStyleConfigHelpers(outcomeCellStyleParts)

export const OutcomeCellStyle = outcomeHelpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'flex',
      height: '3.75rem',
      alignItems: 'center',
      alignContent: 'center',
      gap: '0.5rem',
      flex: '1 0 0',
      flexWrap: 'wrap',
    },
    iconWrapper: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    icon: {
      boxSize: '12px',
    },
    text: {
      textStyle: textStyleKeys.Body1,
    },
  },
  variants: {
    [CallOutcomeCellVariant.InProgress]: {
      text: {
        color: 'blue.900',
      },
      icon: {
        color: 'blue.900',
      },
    },
    [CallOutcomeCellVariant.Success]: {
      text: {
        color: 'gray.1300',
      },
      icon: {
        color: 'gray.1300',
      },
    },
    [CallOutcomeCellVariant.Failure]: {
      text: {
        color: 'red.800',
      },
      icon: {
        color: 'red.800',
      },
    },
  },
  defaultProps: {
    variant: CallOutcomeCellVariant.Success,
  },
})

const outcomeThemeKey = getName({ OutcomeCellStyle })
export const useCallOutcomeCellStyles = (props?: ThemingProps) =>
  useCustomMultiStyleConfig<typeof outcomeCellStyleParts>(
    outcomeThemeKey,
    props,
  )
// #endregion OutcomeCellStyle

// #region VoicemailCellStyle
const voicemailCellStyleParts = ['container', 'icon'] as const
const voicemailHelpers = createMultiStyleConfigHelpers(voicemailCellStyleParts)

export const VoicemailCellStyle = voicemailHelpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'flex',
      height: '3.75rem',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      flex: '1 0 0',
    },
    icon: {
      boxSize: '16px',
    },
  },
})

const voicemailThemeKey = getName({ VoicemailCellStyle })
export const useVoicemailCellStyles = (props?: ThemingProps) =>
  useCustomMultiStyleConfig<typeof voicemailCellStyleParts>(
    voicemailThemeKey,
    props,
  )
// #endregion VoicemailCellStyle

// #region ContactCellStyle
export const ContactCellStyleValues = {
  menuWidth: '13.75rem',
}

const contactCellStyleParts = [
  'container',
  'titleContainer',
  'subtitleContainer',
  'titleIconContainer',
  'titleIcon',
  'titleText',
  'titlePrefix',
  'subtitleText',
] as const

const contactHelpers = createMultiStyleConfigHelpers(contactCellStyleParts)

export const ContactCellStyle = contactHelpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'flex',
      height: '3.75rem',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      flex: '1 0 0',
    },
    titleContainer: {
      display: 'flex',
      height: '1.75rem',
      alignItems: 'center',
      gap: '0.5rem',
      pr: '1rem',
      flexShrink: 0,
      alignSelf: 'stretch',
    },
    subtitleContainer: {
      display: 'flex',
      alignItems: 'center',
      minHeight: '1.5rem',
      gap: '0.5rem',
      flexShrink: 0,
      alignSelf: 'stretch',
      pr: '1rem',
    },
    titleIconContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      boxSize: '1rem',
      borderRadius: '6.25rem',
    },
    titleIcon: {
      boxSize: '16px',
    },
    titleText: {
      textStyle: textStyleKeys.Body1,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    subtitleText: {
      textStyle: textStyleKeys.Body3,
      color: 'gray.800',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    titlePrefix: {
      textStyle: textStyleKeys.Body3,
      color: 'gray.1300',
    },
  },
})

const contactThemeKey = getName({ ContactCellStyle })
export const useContactCellStyles = (props?: ThemingProps) =>
  useCustomMultiStyleConfig<typeof contactCellStyleParts>(
    contactThemeKey,
    props,
  )
// #endregion ContactCellStyle

// #region DurationCellStyle
const durationCellStyleParts = [
  'container',
  'wrapper',
  'text',
  'iconWrapper',
] as const
const durationHelpers = createMultiStyleConfigHelpers(durationCellStyleParts)

export const DurationCellStyle = durationHelpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'flex',
      height: '3.75rem',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      flex: '1 0 0',
    },
    wrapper: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-star',
      justifyContent: 'space-between',
    },
    iconWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
    },
    text: {
      textStyle: textStyleKeys.Body1,
      minWidth: '3.3rem',
      textAlign: 'left',
    },
  },
})

const durationThemeKey = getName({ DurationCellStyle })
export const useDurationCellStyles = (props?: ThemingProps) =>
  useCustomMultiStyleConfig<typeof durationCellStyleParts>(
    durationThemeKey,
    props,
  )
// #endregion DurationCellStyle

// #region TranscriptCellStyle
const transcriptCellStyleParts = [
  'container',
  'wrapper',
  'text',
  'iconWrapper',
] as const
const transcriptHelpers = createMultiStyleConfigHelpers(
  transcriptCellStyleParts,
)

export const TranscriptCellStyle = transcriptHelpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'flex',
      height: '3.75rem',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      flex: '1 0 0',
    },
    wrapper: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
      justifyContent: 'space-between',
    },
    iconWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
    },
    text: {
      textStyle: textStyleKeys.Body1,
      minWidth: '3.3rem',
      textAlign: 'left',
    },
  },
})

const transcriptThemeKey = getName({ TranscriptCellStyle })
export const useTranscriptCellStyles = (props?: ThemingProps) =>
  useCustomMultiStyleConfig<typeof transcriptCellStyleParts>(
    transcriptThemeKey,
    props,
  )
// #endregion TranscriptCellStyle

export const CallsListItemCellStyles = {
  DateTimeCellStyle,
  OutcomeCellStyle,
  VoicemailCellStyle,
  ContactCellStyle,
  DurationCellStyle,
  TranscriptCellStyle,
}
